import { formatDistanceToNow } from "date-fns";
import { CircleDot } from "lucide-react";

import EmptyData from "@/components/state-pages/EmptyData";
import * as Sheet from "@/components/ui/sheet";
import { IProductLogsItem } from "@/interfaces/tools.interface";
import { formatToCapitalizedWords, renderFullName } from "@/lib/utils";
import { GroupedData } from "@/pages/project-register/Activity";
import {
  getClassName,
  highlightTitleInDescription,
} from "@/pages/projects/assign-tools/components/LogSheet";
import {
  DATE_TIME_FORMAT,
  YEAR_MONTH_DAY_FORMAT,
  dateFormat,
  todayYesterDay,
} from "@/utils/helper-functions/date-time.helper";

interface SubProductItemLogSheetProps {
  isOpen: boolean;
  toggleSubProductItemLogSheet: () => void;
  itemName: string;
  data: IProductLogsItem[];
}

const SubProductItemLogSheet = ({
  isOpen,
  toggleSubProductItemLogSheet,
  data,
  itemName,
}: SubProductItemLogSheetProps): JSX.Element => {
  const groupedData: GroupedData[] = [];

  data?.forEach((item) => {
    const found = groupedData?.find(
      (gd) =>
        dateFormat(gd?.date, YEAR_MONTH_DAY_FORMAT) ===
        dateFormat(item?.createdAt, YEAR_MONTH_DAY_FORMAT),
    );
    if (found) {
      found?.activities?.push({
        date: item?.createdAt,
        description: item?.detail,
        action: item?.action,
        activityName: item?.activityEntity,
        by: renderFullName({
          params: {
            firstName: item?.actor?.firstName,
            middleName: item?.actor?.middleName,
            lastName: item?.actor?.lastName,
          },
        }),
        on: "",
      });
    } else {
      groupedData?.push({
        date: item?.createdAt,
        activities: [
          {
            date: item?.createdAt,
            description: item?.detail,
            action: item?.action,
            activityName: item?.activityEntity,
            by: renderFullName({
              params: {
                firstName: item?.actor?.firstName,
                middleName: item?.actor?.middleName,
                lastName: item?.actor?.lastName,
              },
            }),
            on: "",
          },
        ],
      });
    }
  });

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={toggleSubProductItemLogSheet}>
      <Sheet.SheetContent className='min-w-[60dvw] min-h-screen overflow-scroll'>
        <Sheet.SheetHeader className='text-xl font-medium'>
          {itemName}
        </Sheet.SheetHeader>
        <div>
          {groupedData?.length ? (
            groupedData?.map((item, index) => (
              <div
                className='space-y-6 mt-4 text-aldgray'
                key={`${index}-activity`}
              >
                <h1 className='text-lg text-black'>
                  {todayYesterDay(item.date)}
                </h1>
                <div className='relative'>
                  {item.activities?.map((activity, index) => (
                    <div
                      key={index}
                      className='flex justify-between pb-4 relative'
                    >
                      {/* border */}
                      <div className='absolute bg-primary/20 h-full w-[2px] -top-2 left-4  -z-10' />
                      {/* icon */}
                      <div className='flex items-center justify-center gap-4 h-full'>
                        <div className='rounded-full bg-primary-50 dark:bg-background dark:border p-2 text-primary'>
                          <div className='p-1 bg-[#7367F0]/50  rounded-full'>
                            <CircleDot
                              size={12}
                              color='#7367F0'
                              fill='#7367F0'
                            />
                          </div>
                        </div>
                        {/* title */}
                        <div className='space-y-1.5 '>
                          <div className='text-dark-500 text-sm flex items-center gap-3'>
                            <span className='text-dark-200'>
                              <span className={getClassName(activity.action)}>
                                {formatToCapitalizedWords(activity.action)}{" "}
                              </span>
                              {formatToCapitalizedWords(
                                activity?.activityName ?? "",
                              )}
                            </span>
                          </div>
                          <div className='text-xs'>
                            <p className='text-dark-200 font-normal'>
                              <span className='text-dark-200'>
                                {activity.by}{" "}
                                {highlightTitleInDescription(
                                  activity?.description || "",
                                  activity.action,
                                )}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className='text-xs text-gray-400 shrink-0 my-auto'>
                        {dateFormat(activity?.date, DATE_TIME_FORMAT)}{" "}
                        <span className='font-medium text-primary'>
                          (
                          {formatDistanceToNow(activity?.date, {
                            addSuffix: true,
                          })}
                          )
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <EmptyData
              title='Activity'
              subtitle='There is no activity available for this item'
            />
          )}
        </div>
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default SubProductItemLogSheet;
